import React from "react";
import { AlertCircle, CheckCircle2, Loader2, Plus, Save, Trash2 } from "lucide-react";

export const householdSizeOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10+"];

const relationshipOptions = [
  ["", "Relationship optional"],
  ["spouse", "Spouse"],
  ["child", "Child"],
  ["parent", "Parent"],
  ["sibling", "Sibling"],
  ["relative", "Relative"],
  ["other", "Other"],
];

const ageGroupOptions = [
  ["", "Age group optional"],
  ["adult", "Adult"],
  ["teen", "Teen"],
  ["child", "Child"],
  ["infant", "Infant"],
  ["prefer_not_to_say", "Prefer not to say"],
];

export function createBlankHouseholdMember() {
  return {
    firstName: "",
    lastName: "",
    relationship: "",
    ageGroup: "",
    email: "",
    phone: "",
    _isNew: true,
  };
}

export function householdSizeValue(value, memberCount = 0) {
  const numeric = Number(value) || 1;
  const minimum = Math.max(numeric, Number(memberCount || 0) + 1, 1);
  return minimum >= 10 ? "10+" : String(minimum);
}

function fieldValue(value) {
  return String(value || "").trim();
}

export function hasAnyHouseholdValue(member) {
  return Boolean(
    fieldValue(member?.firstName) ||
      fieldValue(member?.lastName) ||
      fieldValue(member?.relationship) ||
      fieldValue(member?.ageGroup) ||
      fieldValue(member?.email) ||
      fieldValue(member?.phone)
  );
}

export function cleanHouseholdRows(members = []) {
  return members
    .map((member) => {
      const cleaned = {};
      if (member.id) {
        cleaned.id = member.id;
      }
      ["firstName", "lastName", "relationship", "ageGroup", "email", "phone"].forEach(
        (field) => {
          const trimmed = fieldValue(member[field]);
          if (trimmed) {
            cleaned[field] = trimmed;
          }
        }
      );
      return cleaned;
    })
    .filter(hasAnyHouseholdValue);
}

function StatusMessage({ status, successText, errorText }) {
  if (status === "saved") {
    return (
      <div className="mt-4 flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
        <span>{successText}</span>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="mt-4 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
        <span>{errorText}</span>
      </div>
    );
  }
  return null;
}

export default function HouseholdEditor({
  title = "Household Information",
  helperText = "Household member details are optional. Blank rows are not saved.",
  addHelpText = "Add a newborn, spouse, child, relative, or another household member.",
  householdSize,
  householdMembers,
  onHouseholdSizeChange,
  onHouseholdMembersChange,
  onSubmit,
  saveStatus = "idle",
  canEdit = true,
  saveLabel = "Save household",
  savingLabel = "Saving household...",
  savedButtonLabel = "Household saved",
  successText = "Household information saved.",
  errorText = "We could not save household information. Please try again.",
}) {
  const members = Array.isArray(householdMembers) ? householdMembers : [];
  const saving = saveStatus === "loading";

  function updateMember(index, field, value) {
    onHouseholdMembersChange(
      members.map((member, memberIndex) =>
        memberIndex === index ? { ...member, [field]: value } : member
      )
    );
  }

  function addMember() {
    onHouseholdMembersChange([...members, createBlankHouseholdMember()]);
    onHouseholdSizeChange(householdSizeValue(householdSize, members.length + 1));
  }

  function removeMember(index) {
    const member = members[index];
    if (
      member?.id &&
      hasAnyHouseholdValue(member) &&
      !window.confirm("Are you sure you want to remove this household member?")
    ) {
      return;
    }
    const nextMembers = members.filter((_, memberIndex) => memberIndex !== index);
    onHouseholdMembersChange(nextMembers);
    onHouseholdSizeChange(householdSizeValue(householdSize, nextMembers.length));
  }

  return (
    <form onSubmit={onSubmit} className="rounded-3xl border border-[#C9A227]/20 bg-white p-6">
      <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
        {title}
      </h2>
      <p className="mt-2 text-sm text-gray-600">{helperText}</p>

      <label className="mt-6 block max-w-xs">
        <span className="mb-2 block text-sm font-medium text-gray-700">
          Household size
        </span>
        <select
          value={householdSize}
          onChange={(event) => onHouseholdSizeChange(event.target.value)}
          disabled={!canEdit || saving}
          className="min-h-[44px] w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227] disabled:bg-gray-100"
        >
          {householdSizeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-6 space-y-5">
        {members.map((member, index) => (
          <div
            key={member.id || `new-${index}`}
            className="rounded-2xl border border-[#C9A227]/20 bg-[#fff8ec] p-4"
          >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-semibold text-[#722F37]">
                Household member {index + 1}
              </p>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => removeMember(index)}
                  disabled={saving}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                >
                  <Trash2 className="h-4 w-4" />
                  Remove
                </button>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_1.3fr_1fr]">
              {[
                ["firstName", "First name"],
                ["lastName", "Last name"],
                ["relationship", "Relationship"],
                ["ageGroup", "Age group"],
                ["email", "Email"],
                ["phone", "Phone"],
              ].map(([field, label]) => (
                <label key={field} className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">
                    {label}
                  </span>
                  {field === "relationship" ? (
                    <select
                      value={member.relationship || ""}
                      onChange={(event) => updateMember(index, field, event.target.value)}
                      disabled={!canEdit || saving}
                      className="min-h-[44px] w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227] disabled:bg-gray-100"
                    >
                      {relationshipOptions.map(([value, optionLabel]) => (
                        <option key={value || "blank"} value={value}>
                          {optionLabel}
                        </option>
                      ))}
                    </select>
                  ) : field === "ageGroup" ? (
                    <select
                      value={member.ageGroup || ""}
                      onChange={(event) => updateMember(index, field, event.target.value)}
                      disabled={!canEdit || saving}
                      className="min-h-[44px] w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227] disabled:bg-gray-100"
                    >
                      {ageGroupOptions.map(([value, optionLabel]) => (
                        <option key={value || "blank"} value={value}>
                          {optionLabel}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field === "email" ? "email" : "text"}
                      value={member[field] || ""}
                      onChange={(event) => updateMember(index, field, event.target.value)}
                      disabled={!canEdit || saving}
                      className="min-h-[44px] w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227] disabled:bg-gray-100"
                    />
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
        {members.length === 0 && (
          <p className="rounded-2xl border border-dashed border-[#C9A227]/40 bg-[#fff8ec] px-4 py-5 text-sm text-gray-600">
            No additional household members are saved yet.
          </p>
        )}
      </div>

      {canEdit && (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              type="button"
              onClick={addMember}
              disabled={saving}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[#722F37]/30 px-4 py-2 text-sm font-semibold text-[#722F37] hover:bg-[#722F37]/10 disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              Add household member
            </button>
            <p className="mt-2 text-xs text-gray-500">{addHelpText}</p>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-70"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? savingLabel : saveStatus === "saved" ? savedButtonLabel : saveLabel}
          </button>
        </div>
      )}

      <StatusMessage status={saveStatus} successText={successText} errorText={errorText} />
    </form>
  );
}
